// One board for a stake table from fare to round (PF-07): the band names the table, the code is big
// enough to read out, the seats are kekes and empty slots, and three steps say where it stands.
// The same board carries the sandbox check and the "nobody else came" offer, so the whole stake
// process looks like one thing. Design system board, band, keke and icons; no new component.
import type { ReactNode } from 'react';
import { S } from '../strings';
import { Html, W } from './wahala';
import './stake-board.css';

export interface StakeStep {
  title: string;
  line?: string;
  /** done: ticked; now: the step in play (its line may count down); next: still to come. */
  state: 'done' | 'now' | 'next';
  /** A design-system icon for the disc while the step isn't done. */
  icon: 'ticket' | 'timer' | 'horn' | 'check' | 'stop';
}

export interface StakeBoardProps {
  band: string;
  /** The band's right side: the game and seats. */
  aside?: string;
  fare?: number;
  code?: string;
  codeHint?: string;
  /** Seats: the filled ones as keke colours (in seat order), the rest drawn empty. */
  seats?: number;
  filled?: string[];
  steps?: StakeStep[];
  /** A message in place of the steps (the check passed, the time ran out, a practice round). */
  note?: { title: string; line: string; tone: 'done' | 'info' };
  /** Live line under the steps (the countdown), polite to screen readers. */
  status?: string;
  children?: ReactNode;
  className?: string;
}

const disc = (s: StakeStep) => W().icon(s.state === 'done' ? 'check' : s.icon, { size: 18 });

export function StakeBoard(p: StakeBoardProps) {
  const seats = Math.max(0, Math.min(4, p.seats ?? 0));
  const filled = p.filled ?? [];
  return (
    <section className={`wh-board stake-board${p.className ? ` ${p.className}` : ''}`} aria-label={p.band}>
      <header className="wh-board__band">
        <h2>{p.band}</h2>
        {p.aside && <span>{p.aside}</span>}
      </header>
      {(p.code || p.fare !== undefined) && (
        <div className="stake-board__top">
          {p.code && (
            <div className="stake-board__code">
              <span className="label">{S.stakes.board.code}</span>
              <strong>{p.code}</strong>
            </div>
          )}
          {p.fare !== undefined && <Html className="stake-board__fare" html={W().fare({ amount: p.fare })} />}
        </div>
      )}
      {p.codeHint && <p className="stake-board__hint">{p.codeHint}</p>}
      {seats > 0 && (
        <ul className="stake-board__seats" aria-label={`${filled.length} of ${seats}`}>
          {Array.from({ length: seats }, (_, i) => {
            const c = filled[i];
            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: seats are positions, not items
              <li key={i} className={c ? 'is-in' : 'is-empty'}>
                {c && <Html html={W().keke({ player: c as never, state: 'idle', size: 56 })} />}
              </li>
            );
          })}
        </ul>
      )}
      {p.note ? (
        <div className={`stake-board__note is-${p.note.tone}`} role="status">
          <strong>{p.note.title}</strong>
          <p>{p.note.line}</p>
        </div>
      ) : (
        p.steps && (
          <ol className="stake-board__steps">
            {p.steps.map((s) => (
              <li key={s.title} className={`is-${s.state}`} aria-current={s.state === 'now' ? 'step' : undefined}>
                <Html className="stake-board__disc" html={disc(s)} />
                <span className="stake-board__step">
                  <strong>{s.title}</strong>
                  {s.line && <span>{s.line}</span>}
                </span>
              </li>
            ))}
          </ol>
        )
      )}
      {p.status !== undefined && (
        <p className="stake-board__status" aria-live="polite">
          {p.status}
        </p>
      )}
      {p.children && <footer className="stake-board__foot">{p.children}</footer>}
    </section>
  );
}
